import React, { useState, useEffect, useMemo } from "react";
import { useApp } from "../context/AppContext";
import Sidebar from "../components/Sidebar";
import PageShell from "../components/PageShell";
import AddShowModal from "../components/AddShowModal";
import { KPI, Tag } from "../components/atoms";
import { colors } from "../theme";
import { getEvents, getShows, getSalesAssignments, getMarketingAssignments } from "../services/api";
import { deriveStage, PIPELINE_STEPS, mapShowRow } from "../config/showStage";
import { Landmark, Building2, Megaphone, Upload, Search, ChevronRight, MapPin, Calendar, Plus } from "lucide-react";
import { FilterSelect, FilterClear } from "../components/FilterBar";

const F = "'Montserrat',sans-serif";
// Type scale (matches the reference tokens) — kept in one place for consistency.
const FS = { page: 20, section: 12, event: 15, show: 14, body: 13, meta: 12 };
const fmtDate = (d) => { if (!d) return null; try { return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); } catch { return d; } };
const initials = (s) => (s || "?").trim().charAt(0).toUpperCase();

// ── Small presentational bits ─────────────────────────────────────────────────
function StageBadge({ stage }) {
  return (
    <span style={{ padding: "3px 10px", borderRadius: 20,
      background: stage.bg, color: stage.c, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
      {stage.label}
    </span>
  );
}

// Company-count chip — shared by the event header and each show row so the icon,
// wording, and right-edge line up into a single tally column.
function CompanyCount({ n }) {
  return (
    <span style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4,
      minWidth: 104, flexShrink: 0, color: colors.text.muted, fontSize: FS.meta }}>
      <Building2 size={12} /> {(n || 0).toLocaleString()} compan{n === 1 ? "y" : "ies"}
    </span>
  );
}

function Pipeline({ step }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", margin: "4px 0 16px" }}>
      {PIPELINE_STEPS.map((label, i) => {
        const on = i < step;
        return (
          <div key={label} style={{ flex: 1, textAlign: "center", position: "relative", fontSize: 11,
            color: on ? colors.text.heading : colors.text.faint, fontWeight: on ? 600 : 400 }}>
            {i > 0 && <div style={{ position: "absolute", top: 7, left: "-50%", width: "100%", height: 3,
              background: on ? colors.status.good : colors.border.subtle, zIndex: 0 }} />}
            <div style={{ width: 14, height: 14, borderRadius: "50%", margin: "0 auto 6px", position: "relative", zIndex: 1,
              background: on ? colors.status.good : colors.border.subtle, border: `3px solid ${colors.surface.card}`,
              boxShadow: `0 0 0 1px ${on ? colors.status.good : colors.border.subtle}` }} />
            {label}
          </div>
        );
      })}
    </div>
  );
}

// Review card — lead count top-right, reviewed progress below (matches the reference)
function ReviewCard({ label, owner, reviewed, total, leadNum, leadLabel }) {
  const pct = total > 0 ? Math.round((reviewed / total) * 100) : 0;
  return (
    <div style={{ flex: 1, minWidth: 200, border: `1px solid ${colors.border.subtle}`, borderRadius: 10, padding: "12px 14px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
        <small style={{ color: colors.text.muted, fontSize: 11 }}>{label} · {owner || "Unassigned"}</small>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1, color: colors.text.heading }}>{leadNum}</div>
          <div style={{ fontSize: 11, color: colors.text.muted }}>{leadLabel}</div>
        </div>
      </div>
      <span style={{ display: "block", marginTop: 6, fontSize: FS.body, fontWeight: 600, color: colors.text.heading }}>{reviewed} / {total} reviewed</span>
      <div style={{ height: 6, background: colors.surface.subtle, borderRadius: 6, marginTop: 7, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: colors.status.good }} />
      </div>
    </div>
  );
}

function SectionLabel({ label, count, right }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: FS.section, fontWeight: 600,
      color: colors.text.muted, textTransform: "uppercase", letterSpacing: 0.5, margin: "18px 2px 8px" }}>
      {label}
      <span style={{ background: colors.surface.subtle, color: colors.text.muted, borderRadius: 20, padding: "1px 8px", fontSize: 11, letterSpacing: 0 }}>{count}</span>
      {right && <span style={{ marginLeft: "auto", textTransform: "none", letterSpacing: 0, fontWeight: 400, fontSize: 11, color: colors.text.faint }}>{right}</span>}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────
export default function PortfolioPage() {
  const {
    nav, selectedEvent, setSelectedEvent, changeEvent,
    currentRole, setCurrentRole, currentUser, currentUserId, showToast,
    notifs, setNotifs, notifOpen, setNotifOpen,
    setCompaniesYear, setCompaniesUi, setPendingInsights,
  } = useApp();

  const [events, setEvents]   = useState([]);
  const [shows, setShows]     = useState([]);
  const [salesA, setSalesA]   = useState([]);
  const [mktA, setMktA]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const [search, setSearch]         = useState("");
  const [yearFilter, setYearFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("any");
  const [openEvent, setOpenEvent]   = useState(null); // single-open accordion — expanding one collapses the rest
  const [openShows, setOpenShows]   = useState(() => new Set());
  const [bucketOpen, setBucketOpen] = useState(false);
  const [addModal, setAddModal]     = useState(null); // null | { eventId }

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getEvents().catch(() => []),
      getShows().catch(() => []),
      getSalesAssignments().catch(() => []),
      getMarketingAssignments().catch(() => []),
    ]).then(([ev, sh, sa, ma]) => {
      setEvents(Array.isArray(ev) ? ev : []);
      setShows((Array.isArray(sh) ? sh : []).map(mapShowRow));
      setSalesA(Array.isArray(sa) ? sa : []);
      setMktA(Array.isArray(ma) ? ma : []);
      setLoading(false);
    });
  }, [refreshKey]);

  const salesFor = (id) => salesA.find(a => a.show_id === id);
  const mktFor   = (id) => mktA.find(a => a.show_id === id);

  const showsByEvent = useMemo(() => {
    const m = {};
    shows.forEach(s => { (m[s.event] = m[s.event] || []).push(s); });
    return m;
  }, [shows]);

  const years = useMemo(() => [...new Set(shows.map(s => s.year).filter(Boolean))].sort((a, b) => b - a), [shows]);

  const totalCompanies = shows.reduce((s, x) => s + (x.companiesFound || 0), 0);
  const totalMQL = mktA.reduce((s, a) => s + (a.pushed_count || 0), 0);
  const totalSQL = salesA.reduce((s, a) => s + (a.approved_count || 0), 0);

  const matchesSearch = (ev) => {
    if (!search) return true;
    const q = search.toLowerCase();
    if ((ev.name || "").toLowerCase().includes(q) || (ev.sector || "").toLowerCase().includes(q)) return true;
    return (showsByEvent[ev.ite_event_id] || []).some(s => s.name.toLowerCase().includes(q));
  };

  const inDateRange = (s) => {
    if (dateFilter === "any") return true;
    if (!s.startDate) return false;
    const start = new Date(s.startDate + "T12:00:00");
    if (isNaN(start)) return false;
    const now = new Date();
    if (dateFilter === "thisYear") return start.getFullYear() === now.getFullYear();
    const months = dateFilter === "3m" ? 3 : dateFilter === "6m" ? 6 : 12;
    const limit = new Date(now); limit.setMonth(limit.getMonth() + months);
    return start >= now && start <= limit;
  };

  const showPasses = (s) => {
    if (yearFilter !== "all" && String(s.year) !== String(yearFilter)) return false;
    if (stageFilter !== "all" && deriveStage(s, salesFor(s.id), mktFor(s.id)).key !== stageFilter) return false;
    if (!inDateRange(s)) return false;
    return true;
  };

  const activeEvents = events
    .filter(e => (showsByEvent[e.ite_event_id] || []).length > 0 && matchesSearch(e))
    .sort((a, b) => (b.total_companies || 0) - (a.total_companies || 0));
  const notTracked = events
    .filter(e => (showsByEvent[e.ite_event_id] || []).length === 0 && matchesSearch(e))
    .sort((a, b) => a.name.localeCompare(b.name));

  const toggle = (setFn) => (id) => setFn(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleShow  = toggle(setOpenShows);
  // Single-open: opening an event collapses whichever one was open before.
  const toggleEvent = (id) => setOpenEvent(prev => (prev === id ? null : id));

  const viewCompanies = (s, eventName) => {
    setCompaniesUi({
      filters:  { year: String(s.year || ""), ite_event: eventName || "", show: s.name || "" },
      search:   "",
      sort:     { by: "name", dir: "asc" },
      viewMode: "card",
    });
    setSelectedEvent(s.event);
    nav("companies");
  };
  const viewAnalytics = (s) => { setPendingInsights({ ite_event_id: s.event, show_id: s.id, year: s.year }); nav("insights"); };

  const actionLink = { background: "none", border: "none", padding: 0, color: colors.brand.primary,
    fontWeight: 600, fontSize: FS.body, cursor: "pointer", fontFamily: F };
  const primaryBtn = { display: "flex", alignItems: "center", gap: 5, padding: "8px 16px", borderRadius: 9,
    border: "none", background: colors.brand.primary, color: "#fff", fontSize: FS.body, fontWeight: 700, cursor: "pointer", fontFamily: F };
  return (
    <PageShell>
      <Sidebar active="portfolio" onNav={nav} selectedEvent={selectedEvent} onChangeEvent={changeEvent}
        notifs={notifs} setNotifs={setNotifs} notifOpen={notifOpen} setNotifOpen={setNotifOpen}
        currentRole={currentRole} setCurrentRole={setCurrentRole} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "16px 28px", background: colors.surface.card, borderBottom: `1px solid ${colors.border.subtle}` }}>
          <div style={{ fontSize: FS.page, fontWeight: 800, letterSpacing: -0.2 }}>Competitor Shows</div>
          <div style={{ fontSize: FS.body, color: colors.text.muted, marginTop: 2 }}>
            Track competitor shows and review progress across your events.
          </div>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: "16px 28px 40px" }}>
          <style>{`
            .pf-head:hover { background: ${colors.surface.hover}; }
            .pf-urow .pf-add { opacity: 0; transition: opacity .12s; }
            .pf-urow:hover .pf-add { opacity: 1; }
            .pf-urow:hover { background: ${colors.surface.hover}; }
          `}</style>

          {/* KPIs */}
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <KPI icon={<Landmark size={22} color={colors.brand.primary} />} label="Competitor shows" value={shows.length} sub="tracked across all events" color={colors.brand.primary} />
            <KPI icon={<Building2 size={22} color={colors.brand.orange} />} label="Companies found" value={totalCompanies.toLocaleString()} sub="across all competitor shows" color={colors.brand.orange} />
            <KPI icon={<Megaphone size={22} color={colors.status.bad} />} label="Marketing leads" value={totalMQL} sub="across all competitor shows" color={colors.status.bad} />
            <KPI icon={<Upload size={22} color={colors.status.good} />} label="Sales-ready leads" value={totalSQL} sub="across all competitor shows" color={colors.status.good} />
          </div>

          {/* Filters */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
            <div style={{ position: "relative", flex: "0 0 auto" }}>
              <Search size={13} style={{ position: "absolute", left: 10, top: 9, color: colors.text.faint }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search events or shows…"
                style={{ padding: "7px 12px 7px 30px", borderRadius: 8, border: "1px solid #D8DADC",
                  fontSize: 11, fontFamily: F, outline: "none", minWidth: 220, color: "#26262A" }} />
            </div>
            <FilterSelect value={yearFilter} onChange={e => setYearFilter(e.target.value)} active={yearFilter !== "all"}>
              <option value="all">All Years</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </FilterSelect>
            <FilterSelect value={stageFilter} onChange={e => setStageFilter(e.target.value)} active={stageFilter !== "all"}>
              <option value="all">All Stages</option>
              <option value="new">New</option>
              <option value="awaiting">Awaiting Review</option>
              <option value="review">In Review</option>
              <option value="done">Completed</option>
            </FilterSelect>
            <FilterSelect value={dateFilter} onChange={e => setDateFilter(e.target.value)} title="Show date range" active={dateFilter !== "any"}>
              <option value="any">Any dates</option>
              <option value="3m">Next 3 months</option>
              <option value="6m">Next 6 months</option>
              <option value="12m">Next 12 months</option>
              <option value="thisYear">This year</option>
            </FilterSelect>
            {(yearFilter !== "all" || stageFilter !== "all" || dateFilter !== "any") && (
              <FilterClear onClick={() => { setYearFilter("all"); setStageFilter("all"); setDateFilter("any"); }} />
            )}
            <button onClick={() => setAddModal({ eventId: "" })} style={{ ...primaryBtn, marginLeft: "auto" }}>
              <Plus size={14} /> Add Competitor Show
            </button>
          </div>

          {loading ? (
            <div style={{ padding: "60px", textAlign: "center", color: colors.text.muted, fontSize: FS.body }}>Loading competitor shows…</div>
          ) : (<>
            {/* Active events */}
            <SectionLabel label="Active" count={activeEvents.length}
              right={`${events.length} event${events.length !== 1 ? "s" : ""} tracked in total`} />
            {activeEvents.length === 0 && (
              <div style={{ padding: "28px", textAlign: "center", color: colors.text.faint, fontSize: FS.body,
                background: colors.surface.card, border: `1px solid ${colors.border.subtle}`, borderRadius: 12 }}>
                No tracked events match your filters.
              </div>
            )}
            {activeEvents.map(ev => {
              const evShows = (showsByEvent[ev.ite_event_id] || []).filter(showPasses).sort((a, b) => (b.year || 0) - (a.year || 0));
              if (evShows.length === 0) return null;
              const evOpen = openEvent === ev.ite_event_id;
              const evCompanies = evShows.reduce((n, s) => n + (s.companiesFound || 0), 0);
              return (
                <div key={ev.ite_event_id} style={{ background: colors.surface.card, border: `1px solid ${colors.border.subtle}`, borderRadius: 12, marginBottom: 10, overflow: "hidden" }}>
                  {/* Event header */}
                  <div className="pf-head" onClick={() => toggleEvent(ev.ite_event_id)} style={{ display: "flex", alignItems: "center", gap: 13, padding: "14px 18px", cursor: "pointer" }}>
                    <ChevronRight size={14} color={colors.text.muted} style={{ transform: evOpen ? "rotate(90deg)" : "none", transition: "transform .15s", flexShrink: 0 }} />
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: colors.brand.primaryTint, color: colors.brand.primary, display: "grid", placeItems: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{initials(ev.name)}</div>
                    <div style={{ minWidth: 0 }}>
                      <span style={{ fontSize: FS.event, fontWeight: 600 }}>{ev.name}</span>
                    </div>
                    <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 16, color: colors.text.muted, fontSize: FS.meta, flexShrink: 0 }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Landmark size={12} /> {evShows.length} show{evShows.length !== 1 ? "s" : ""}</span>
                      <CompanyCount n={evCompanies} />
                    </div>
                  </div>

                  {/* Shows */}
                  {evOpen && (
                    <div style={{ borderTop: `1px solid ${colors.border.subtle}`, background: colors.surface.page }}>
                      {evShows.map(s => {
                        const sA = salesFor(s.id), mA = mktFor(s.id);
                        const stage = deriveStage(s, sA, mA);
                        const sOpen = openShows.has(s.id);
                        const dateMeta = fmtDate(s.startDate) && `${fmtDate(s.startDate)}${s.endDate ? " – " + fmtDate(s.endDate) : ""}`;
                        return (
                          <div key={s.id} style={{ borderBottom: `1px solid ${colors.border.subtle}` }}>
                            <div onClick={() => toggleShow(s.id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px 12px 40px", cursor: "pointer" }}>
                              <ChevronRight size={12} color={colors.text.muted} style={{ transform: sOpen ? "rotate(90deg)" : "none", transition: "transform .15s", flexShrink: 0 }} />
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: stage.dot, flexShrink: 0 }} />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: FS.show, fontWeight: 600 }}>{s.name}</div>
                                <div style={{ display: "flex", gap: 14, color: colors.text.muted, fontSize: FS.meta, flexWrap: "wrap", marginTop: 2 }}>
                                  {s.location && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><MapPin size={11} /> {s.location}</span>}
                                  {dateMeta && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Calendar size={11} /> {dateMeta}</span>}
                                </div>
                              </div>
                              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
                                <StageBadge stage={stage} />
                                <CompanyCount n={s.companiesFound} />
                              </div>
                            </div>

                            {/* Progress panel */}
                            {sOpen && (
                              <div style={{ padding: "2px 22px 18px 40px", background: colors.surface.card, borderTop: `1px dashed ${colors.border.subtle}` }}>
                                <div style={{ fontSize: FS.meta, fontWeight: 600, color: colors.text.muted, textTransform: "uppercase", letterSpacing: 0.5, margin: "14px 0 10px" }}>Review stage</div>
                                <Pipeline step={stage.step} />
                                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                                  <ReviewCard label="Sales review" owner={sA?.owner_name}
                                    reviewed={sA?.reviewed_count || 0} total={sA?.total_companies || s.companiesFound || 0}
                                    leadNum={sA?.approved_count || 0} leadLabel="sales-ready leads" />
                                  <ReviewCard label="Marketing review" owner={mA?.owner_name}
                                    reviewed={mA?.reviewed_count || 0} total={mA?.total_companies || s.companiesFound || 0}
                                    leadNum={mA?.pushed_count || 0} leadLabel="marketing leads" />
                                </div>
                                <div style={{ display: "flex", justifyContent: "flex-end", gap: 18 }}>
                                  <button onClick={() => viewCompanies(s, ev.name)} style={actionLink}>View {s.companiesFound} companies →</button>
                                  <button onClick={() => viewAnalytics(s)} style={actionLink}>View analytics →</button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Not yet tracked */}
            {notTracked.length > 0 && (<>
              <SectionLabel label="Not yet tracked" count={notTracked.length} />
              <div style={{ background: colors.surface.card, border: `1px solid ${colors.border.subtle}`, borderRadius: 12, overflow: "hidden" }}>
                <div className="pf-head" onClick={() => setBucketOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", cursor: "pointer" }}>
                  <ChevronRight size={14} color={colors.text.muted} style={{ transform: bucketOpen ? "rotate(90deg)" : "none", transition: "transform .15s" }} />
                  <b style={{ fontSize: FS.event, fontWeight: 600 }}>{notTracked.length} event{notTracked.length !== 1 ? "s" : ""} not yet tracked</b>
                  <span style={{ marginLeft: "auto", color: colors.text.muted, fontSize: FS.body }}>Hover a row to add its competitor show</span>
                </div>
                {bucketOpen && (
                  <div style={{ borderTop: `1px solid ${colors.border.subtle}` }}>
                    {notTracked.map(ev => (
                      <div key={ev.ite_event_id} className="pf-urow"
                        style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 18px 11px 44px",
                          borderBottom: `1px solid ${colors.border.subtle}`, fontSize: FS.body }}>
                        <span style={{ flex: 1 }}>{ev.name}</span>
                        {ev.sector && <span style={{ color: colors.text.muted, fontSize: FS.meta }}>{ev.sector}</span>}
                        <button className="pf-add" onClick={() => setAddModal({ eventId: ev.ite_event_id })}
                          style={{ ...actionLink, fontSize: FS.meta, whiteSpace: "nowrap" }}>
                          + Add Competitor Show →
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>)}
          </>)}
        </div>
      </div>

      <AddShowModal
        open={!!addModal}
        defaultEventId={addModal?.eventId}
        existingShows={shows}
        scheduledBy={currentUserId || currentUser}
        onToast={showToast}
        onClose={() => setAddModal(null)}
        onAdded={() => setRefreshKey(k => k + 1)}
      />
    </PageShell>
  );
}
